import { useState, useEffect, useRef, useCallback } from "react";
import JsSIP from "jssip";
import { Phone, PhoneOff, PhoneCall, PhoneIncoming, X, Delete, Clock, ChevronLeft, Volume2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function SipDialer({ sip }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("offline");
  const [detail, setDetail] = useState("");
  const [num, setNum] = useState("");
  const [call, setCall] = useState(null);
  const [history, setHistory] = useState(() => { try { return JSON.parse(localStorage.getItem("fp_sip_history") || "[]"); } catch { return []; } });
  const [showHist, setShowHist] = useState(false);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [audioDbg, setAudioDbg] = useState("");
  const [callEvents, setCallEvents] = useState("");
  const uaRef = useRef(null);
  const sessionRef = useRef(null);
  const audioRef = useRef(null);
  const pcRef = useRef(null);
  const answeringRef = useRef(false);

  const pushHistory = useCallback((n, dir) => {
    if (!n) return;
    setHistory((h) => {
      const next = [{ num: String(n), dir, ts: Date.now() }, ...h].slice(0, 20);
      try { localStorage.setItem("fp_sip_history", JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!sip || !sip.enabled || !sip.ws_url || !sip.uri) { setStatus("offline"); setDetail(""); return; }
    let ua;
    try {
      JsSIP.debug.disable();
      const socket = new JsSIP.WebSocketInterface(sip.ws_url);
      ua = new JsSIP.UA({ sockets: [socket], uri: sip.uri, password: sip.password || "", display_name: sip.display_name || "", session_timers: false, register: true });
      uaRef.current = ua;
      setStatus("connecting"); setDetail("");
      ua.on("connected", () => { setStatus("connecting"); setDetail(""); });
      ua.on("registered", () => { setStatus("registered"); setDetail(""); });
      ua.on("unregistered", () => setStatus("offline"));
      ua.on("registrationFailed", (e) => {
        setStatus("failed");
        const code = e && e.response && e.response.status_code;
        const cause = (e && e.cause) || "";
        // eslint-disable-next-line no-console
        console.error("[SIP] registrationFailed", { code, cause, response: e && e.response });
        if (code === 401 || code === 403 || /auth/i.test(String(cause))) setDetail("Serwer odrzucił login lub hasło (kod " + (code || "401") + "). Sprawdź dane konta SIP.");
        else if (/timeout/i.test(String(cause))) setDetail("Serwer nie odpowiedział na rejestrację (timeout). Sprawdź adres/port WSS.");
        else setDetail("Rejestracja odrzucona" + (cause ? ": " + cause : "") + (code ? " (kod " + code + ")" : ""));
      });
      ua.on("disconnected", (e) => {
        setStatus("failed");
        // eslint-disable-next-line no-console
        console.error("[SIP] disconnected", e);
        setDetail("Nie można połączyć z serwerem WSS (" + (sip.ws_url) + "). Najczęściej: zły port, zapora, lub certyfikat serwera nie jest zaufany przez przeglądarkę.");
      });
      ua.on("newRTCSession", (e) => {
        const s = e.session; sessionRef.current = s;
        const dir = s.direction;
        const remote = (s.remote_identity && s.remote_identity.uri && s.remote_identity.uri.user)
          || (s.remote_identity && s.remote_identity.display_name) || "Nieznany";
        setCall({ dir, state: dir === "incoming" ? "ringing" : "calling", remote });
        if (dir === "incoming") {
          api.get(`/sip/lookup?number=${encodeURIComponent(remote)}`).then(({ data }) => {
            if (data && data.found) {
              setCall((c) => (c ? { ...c, clientName: data.name, clientAddr: data.address } : c));
              if (data.lat != null && data.lng != null) {
                window.dispatchEvent(new CustomEvent("fp-sip-incoming", { detail: { id: data.id, lat: data.lat, lng: data.lng, name: data.name } }));
              }
            }
          }).catch(() => { /* noop */ });
        }
        setCallEvents(dir === "incoming" ? "przychodzące" : "wychodzące");
        const logEv = (t) => setCallEvents((p) => (p ? p + " → " : "") + t);
        s.on("progress", () => logEv("dzwoni(progress)"));
        s.on("ended", () => { logEv("zakończone"); setCall(null); sessionRef.current = null; pcRef.current = null; answeringRef.current = false; window.dispatchEvent(new CustomEvent("fp-sip-callend")); });
        s.on("failed", (ev) => { const cause = (ev && ev.cause) || ""; logEv("NIEUDANE:" + cause); if (cause && !/canceled|terminated|bye/i.test(String(cause))) toast.error("Połączenie nieudane: " + cause); setCall(null); sessionRef.current = null; pcRef.current = null; answeringRef.current = false; window.dispatchEvent(new CustomEvent("fp-sip-callend")); });
        const playRemote = () => {
          if (!audioRef.current) return;
          audioRef.current.muted = false;
          audioRef.current.volume = 1;
          const p = audioRef.current.play();
          if (p && p.catch) p.then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
        };
        const attachRemote = () => {
          const pc = pcRef.current || (sessionRef.current && sessionRef.current.connection);
          if (!pc || !audioRef.current) return;
          pcRef.current = pc;
          const rs = new MediaStream();
          pc.getReceivers().forEach((r) => { if (r.track && r.track.kind === "audio") rs.addTrack(r.track); });
          if (rs.getTracks().length) { audioRef.current.srcObject = rs; playRemote(); }
        };
        s.on("accepted", () => { logEv("accepted"); setCall((c) => (c ? { ...c, state: "in-call" } : c)); setTimeout(attachRemote, 400); });
        s.on("confirmed", () => { logEv("confirmed(200OK)"); setCall((c) => (c ? { ...c, state: "in-call" } : c)); setTimeout(attachRemote, 400); });
        s.on("peerconnection", (ev) => {
          const pc = ev.peerconnection; pcRef.current = pc;
          pc.addEventListener("track", (te) => {
            const stream = (te.streams && te.streams[0]) || new MediaStream([te.track]);
            if (audioRef.current) { audioRef.current.srcObject = stream; playRemote(); }
          });
        });
        if (dir === "incoming") { pushHistory(remote, "in"); setOpen(true); }
      });
      ua.start();
    } catch (err) {
      setStatus("failed");
      setDetail("Błędny adres WSS lub konfiguracja: " + (err && err.message ? err.message : "sprawdź adres WebSocket"));
    }
    return () => { try { ua && ua.stop(); } catch { /* noop */ } uaRef.current = null; };
  }, [sip && sip.enabled, sip && sip.ws_url, sip && sip.uri, sip && sip.password]);

  const iceServers = [{ urls: "stun:stun.l.google.com:19302" }];
  if (sip && sip.turn_host && sip.turn_user) {
    iceServers.push({ urls: [`turn:${sip.turn_host}:3478?transport=udp`, `turn:${sip.turn_host}:3478?transport=tcp`], username: sip.turn_user, credential: sip.turn_pass || "" });
  }
  const opts = { mediaConstraints: { audio: true, video: false }, pcConfig: { iceServers } };
  const doCall = useCallback((target) => {
    if (!uaRef.current || status !== "registered") { toast.error("Telefon niepołączony"); return; }
    const domain = (sip.uri.split("@")[1] || "").replace(/^sip:/, "");
    const dst = target.includes("@") || target.startsWith("sip:") ? target : `sip:${target}@${domain}`;
    pushHistory(target, "out");
    try { uaRef.current.call(dst, opts); } catch { toast.error("Nie udało się zadzwonić"); }
  }, [status, sip, pushHistory]);

  useEffect(() => {
    const handler = (e) => {
      const raw = e.detail && e.detail.number;
      if (!raw) return;
      const clean = String(raw).replace(/[^0-9+*#@.a-zA-Z:]/g, "");
      setOpen(true);
      setNum(clean);
      doCall(clean);
    };
    window.addEventListener("fp-sip-call", handler);
    return () => window.removeEventListener("fp-sip-call", handler);
  }, [doCall]);

  useEffect(() => {
    if (!call || call.state !== "in-call") { setAudioDbg(""); return; }
    const id = setInterval(() => {
      const pc = pcRef.current || (sessionRef.current && sessionRef.current.connection);
      const a = audioRef.current;
      if (!pc) { setAudioDbg("pc=null (czekam na połączenie WebRTC)"); return; }
      pcRef.current = pc;
      const rx = pc.getReceivers().filter((r) => r.track && r.track.kind === "audio");
      if (a && rx.length && (!a.srcObject || a.srcObject.getAudioTracks().length === 0)) {
        const rs = new MediaStream();
        rx.forEach((r) => rs.addTrack(r.track));
        a.srcObject = rs;
        a.muted = false; a.volume = 1;
        a.play().then(() => setAudioBlocked(false)).catch(() => setAudioBlocked(true));
      }
      let s = `audio rx:${rx.length}`;
      if (rx[0] && rx[0].track) s += ` muted:${rx[0].track.muted} ${rx[0].track.readyState}`;
      s += ` | ice:${pc.iceConnectionState} dtls:${pc.connectionState}`;
      if (a) s += ` | audio:${a.paused ? "pauza" : "gra"} src:${a.srcObject ? "tak" : "nie"}`;
      try {
        const sdp = (pc.remoteDescription && pc.remoteDescription.sdp) || "";
        const cands = [...sdp.matchAll(/a=candidate:\S+ \d+ \S+ \d+ (\S+) (\d+) typ (\w+)/g)].map((m) => `${m[3]} ${m[1]}:${m[2]}`);
        if (cands.length) s += ` | zdalne ICE (Asterisk): ${[...new Set(cands)].join(", ")}`;
        else s += " | zdalne ICE: brak w SDP";
        const lsdp = (pc.localDescription && pc.localDescription.sdp) || "";
        const lc = [...lsdp.matchAll(/a=candidate:\S+ \d+ \S+ \d+ (\S+) (\d+) typ (\w+)/g)].map((m) => `${m[3]} ${m[1]}:${m[2]}`);
        if (lc.length) s += ` | lokalne ICE (przeglądarka): ${[...new Set(lc)].join(", ")}`;
        else s += " | lokalne ICE: brak w SDP";
      } catch { /* noop */ }
      s += ` | TURN cfg:${(sip && sip.turn_host) ? sip.turn_host : "BRAK"}`;
      setAudioDbg(s);
    }, 1000);
    return () => clearInterval(id);
  }, [call && call.state]);

  useEffect(() => {
    if (!(call && call.dir === "incoming" && call.state === "ringing")) return;
    let ctx, osc, timer, stopped = false;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = () => {
        if (stopped || !ctx) return;
        try {
          osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine"; osc.frequency.value = 425; gain.gain.value = 0.18;
          osc.connect(gain); gain.connect(ctx.destination);
          osc.start();
          setTimeout(() => { try { osc.stop(); } catch { /* noop */ } }, 1000);
        } catch { /* noop */ }
      };
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      beep();
      timer = setInterval(beep, 5000);
    } catch { /* noop */ }
    return () => { stopped = true; clearInterval(timer); try { osc && osc.stop(); } catch { /* noop */ } try { ctx && ctx.close(); } catch { /* noop */ } };
  }, [call && call.dir, call && call.state]);
  const answer = async () => {
    const s = sessionRef.current;
    if (!s) { toast.error("Brak połączenia do odebrania"); return; }
    if (answeringRef.current) return;
    answeringRef.current = true;
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      answeringRef.current = false;
      toast.error("Brak dostępu do mikrofonu — zezwól na mikrofon w przeglądarce i spróbuj ponownie");
      return;
    }
    setCall((c) => (c ? { ...c, state: "in-call" } : c));
    setCallEvents((p) => (p ? p + " → " : "") + "answer()");
    try { s.answer({ mediaStream: stream, pcConfig: opts.pcConfig }); }
    catch (err) {
      answeringRef.current = false;
      // eslint-disable-next-line no-console
      console.error("[SIP] answer failed", err);
      toast.error("Nie udało się odebrać: " + (err && err.message ? err.message : "błąd audio/mikrofonu"));
    }
  };
  const hangup = () => { try { sessionRef.current && sessionRef.current.terminate(); } catch { /* noop */ } };

  const statusText = { offline: "Nieaktywny", connecting: "Łączenie…", registered: "Gotowy", failed: "Błąd połączenia" }[status];
  const statusColor = { offline: "text-slate-400", connecting: "text-amber-400", registered: "text-green-400", failed: "text-red-400" }[status];
  const tabColor = { registered: "text-green-400", connecting: "text-amber-400", failed: "text-red-400", offline: "text-slate-400" }[status];
  const callStateText = { ringing: "Dzwoni", calling: "Wywołanie", "in-call": "Rozmowa" };

  return (
    <>
      <audio ref={audioRef} autoPlay playsInline />
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="sip-fab"
          className={`absolute right-0 z-[744] bg-slate-900/90 backdrop-blur border border-r-0 border-slate-700 rounded-l-lg px-1.5 py-4 hover:bg-slate-800 flex flex-col items-center gap-1 ${call ? "ring-2 ring-green-500 animate-pulse" : ""} ${tabColor}`}
          style={{ top: "calc(50% + 6px)" }} title="Telefon VoIP">
          <ChevronLeft size={16} />
          {call ? <PhoneCall size={16} className="animate-pulse" /> : <Phone size={16} />}
        </button>
      )}

      {open && (
        <div data-testid="sip-panel" className="absolute top-0 right-0 bottom-0 w-full sm:w-[340px] z-[770] bg-slate-900 border-l border-slate-700 flex flex-col shadow-2xl">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
            <Phone size={18} className="text-green-400" />
            <h3 className="font-heading font-bold text-sm flex-1 truncate">Telefon VoIP</h3>
            <span className="text-[9px] text-cyan-500 font-mono" data-testid="sip-build">AUDIO-v15</span>
            <button onClick={() => setOpen(false)} data-testid="sip-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>

          <div className="px-4 pt-3">
            <div className={`text-xs ${statusColor}`} data-testid="sip-status">● {statusText}</div>
            {detail && <div className="mt-1 text-[11px] text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2" data-testid="sip-detail">{detail}</div>}
          </div>

          <div className="flex-1 overflow-y-auto p-4 pt-3">
          {call ? (
            <div className="text-center space-y-3 mt-6">
              <div className="text-sm text-slate-300">{call.dir === "incoming" ? "Połączenie przychodzące" : "Dzwonię…"}</div>
              <div className="text-2xl font-semibold" data-testid="sip-remote">{call.remote || "—"}</div>
              {(call.clientName || call.clientAddr) && (
                <div data-testid="sip-caller-info" className="mx-auto max-w-[260px] rounded-lg bg-cyan-500/10 border border-cyan-500/30 px-3 py-2">
                  {call.clientName && <div className="text-sm font-semibold text-cyan-200" data-testid="sip-caller-name">{call.clientName}</div>}
                  {call.clientAddr && <div className="text-xs text-slate-300 mt-0.5" data-testid="sip-caller-addr">{call.clientAddr}</div>}
                </div>
              )}
              <div className="text-xs text-slate-400">{callStateText[call.state]}</div>
              {callEvents && <div data-testid="sip-events" className="text-[10px] text-slate-500 break-all px-2 py-1">{callEvents}</div>}
              <div className="flex justify-center gap-4 pt-2">
                {call.dir === "incoming" && call.state === "ringing" && (
                  <button onClick={answer} data-testid="sip-answer" className="w-16 h-16 rounded-full bg-green-600 hover:bg-green-500 flex items-center justify-center"><PhoneIncoming size={26} /></button>
                )}
                <button onClick={hangup} data-testid="sip-hangup" className="w-16 h-16 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center"><PhoneOff size={26} /></button>
              </div>
              {call.state === "in-call" && (
                <button
                  onClick={() => {
                    const a = audioRef.current, pc = pcRef.current || (sessionRef.current && sessionRef.current.connection);
                    let info = "";
                    if (!pc) info = "brak połączenia (pc=null)";
                    else {
                      const rx = pc.getReceivers().filter((r) => r.track && r.track.kind === "audio");
                      info = `ścieżki audio: ${rx.length}`;
                      if (rx[0] && rx[0].track) info += ` | muted:${rx[0].track.muted} | stan:${rx[0].track.readyState}`;
                      try { const rs = new MediaStream(); rx.forEach((r) => rs.addTrack(r.track)); if (rs.getTracks().length && a) a.srcObject = rs; } catch { /* noop */ }
                    }
                    if (a) {
                      a.muted = false; a.volume = 1;
                      a.play().then(() => { setAudioBlocked(false); setAudioDbg(info + " | play:OK"); })
                        .catch((e) => { setAudioDbg(info + " | play:" + (e && e.name ? e.name : "błąd")); });
                    } else setAudioDbg(info + " | brak elementu audio");
                  }}
                  data-testid="sip-enable-audio"
                  className={`mx-auto flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${audioBlocked ? "bg-amber-500 text-slate-900 animate-pulse" : "bg-slate-700 hover:bg-slate-600 text-slate-200"}`}>
                  <Volume2 size={18} /> {audioBlocked ? "Kliknij, aby włączyć dźwięk" : "Włącz dźwięk"}
                </button>
              )}
              {audioDbg && call.state === "in-call" && (
                <div data-testid="sip-audio-dbg" className="mt-2 text-[10px] text-slate-400 bg-slate-800/70 border border-slate-700 rounded px-2 py-1 break-all">{audioDbg}</div>
              )}
            </div>
          ) : (
            <>
              <input value={num} onChange={(e) => setNum(e.target.value.replace(/[^0-9+*#]/g, ""))} placeholder="Numer / SIP" data-testid="sip-number"
                className="w-full text-center text-xl tracking-wide bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 mb-3" />
              <div className="grid grid-cols-3 gap-2 mb-3">
                {["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => (
                  <button key={d} onClick={() => setNum((n) => n + d)} data-testid={`sip-key-${d}`}
                    className="py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-lg font-medium">{d}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setNum((n) => n.slice(0, -1))} data-testid="sip-del" className="px-3 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center"><Delete size={18} /></button>
                <button onClick={() => doCall(num)} disabled={!num || status !== "registered"} data-testid="sip-call"
                  className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-500 disabled:opacity-40 flex items-center justify-center gap-2 font-medium"><PhoneCall size={18} /> Zadzwoń</button>
              </div>
              {status === "failed" && (
                <div className="mt-3 text-[11px] text-slate-400 bg-slate-800/60 border border-slate-700 rounded p-2 space-y-1" data-testid="sip-help">
                  <div className="text-slate-300 font-semibold">Nie można się połączyć — sprawdź:</div>
                  <div>• Adres WebSocket (WSS) i port — u wielu operatorów to <code>wss://serwer:8089/ws</code>, ale bywa inny.</div>
                  <div>• Certyfikat serwera SIP musi być zaufany. Otwórz w nowej karcie <code>https://{(sip.ws_url || "").replace(/^wss?:\/\//, "").split("/")[0]}</code> i zaakceptuj certyfikat.</div>
                  <div>• Login i hasło SIP (kod 401/403 = błędne dane).</div>
                </div>
              )}
              {history.length > 0 && (
                <div className="mt-3 border-t border-slate-700 pt-2">
                  <button onClick={() => setShowHist((s) => !s)} data-testid="sip-history-toggle"
                    className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1"><Clock size={12} /> Ostatnie połączenia ({history.length})</button>
                  {showHist && (
                    <div className="mt-1 max-h-56 overflow-y-auto space-y-0.5" data-testid="sip-history">
                      {history.map((h, i) => (
                        <button key={i} onClick={() => { setNum(h.num); doCall(h.num); }} data-testid={`sip-history-${i}`}
                          className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-slate-800 text-sm">
                          {h.dir === "in" ? <PhoneIncoming size={13} className="text-cyan-400 shrink-0" /> : <PhoneCall size={13} className="text-green-400 shrink-0" />}
                          <span className="flex-1 truncate">{h.num}</span>
                          <span className="text-[10px] text-slate-500 shrink-0">{new Date(h.ts).toLocaleTimeString().slice(0, 5)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          </div>
        </div>
      )}
    </>
  );
}
